import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { apiFetch } from '../utils/api';

interface User {
    id: string;
    email: string;
    full_name?: string;
    is_active: boolean;
    is_verified: boolean;
    is_admin?: boolean;
    is_superuser?: boolean;
    cost_center_id?: string;
    language: string;
    created_at?: string;
    cost_center?: {
        id: string;
        name: string;
    };
}

interface CurrentUserContextType {
    currentUser: User | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    isAdmin: boolean;
    isSuperuser: boolean;
}

// Create context
const CurrentUserContext = createContext<CurrentUserContextType | undefined>(undefined);

interface CurrentUserProviderProps {
    children: ReactNode;
}

// Provider component
export function CurrentUserProvider({ children }: CurrentUserProviderProps) {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCurrentUser = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiFetch('/api/auth/me');
            if (response.ok) {
                const userData = await response.json();
                setCurrentUser(userData);
            } else {
                setError('Failed to fetch user information');
                setCurrentUser(null);
            }
        } catch (err) {
            console.error('Error fetching current user:', err);
            setError('Failed to fetch user information');
            setCurrentUser(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrentUser();
    }, []);

    const value: CurrentUserContextType = {
        currentUser,
        loading,
        error,
        refetch: fetchCurrentUser,
        isAdmin: currentUser?.is_admin || false,
        isSuperuser: currentUser?.is_superuser || false
    };

    return (
        <CurrentUserContext.Provider value={value}>
            {children}
        </CurrentUserContext.Provider>
    );
}

// Hook to use current user
export function useCurrentUser(): CurrentUserContextType {
    const context = useContext(CurrentUserContext);
    if (context === undefined) {
        throw new Error('useCurrentUser must be used within a CurrentUserProvider');
    }
    return context;
}
